import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconEdit,
  IconPlus,
  IconPrinter,
  IconTrash,
} from "@tabler/icons-react";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  createDeviation,
  deleteDeviation,
  getDeviation,
  getDeviationMetadata,
  getDeviations,
  updateDeviation,
} from "../../../services/ObjectDeviationService";
import {
  ObjectDeviationListItemType,
  ObjectDeviationMetadataType,
  ObjectDeviationType,
  UpsertObjectDeviationRequest,
} from "../../../types/ObjectDeviation";
import {
  formatFinnishDate,
  generateTimestamp,
  sanitizeForFilename,
} from "../../../utilities/PdfUtils";
import DeviationReportPDF from "../../pdf/DeviationReportPDF";
import AddEditDeviationModal from "./modals/AddEditDeviationModal";
import styles from "./DeviationsTab.module.css";

interface DeviationsTabProps {
  form: any;
  addressSystemId?: number;
  userId?: number;
}

const emptyMetadata: ObjectDeviationMetadataType = {
  types: [],
  targets: [],
  otherTargetId: 0,
};

const getErrorMessage = (error: unknown, fallback: string) => {
  const candidate = error as {
    response?: { data?: { message?: unknown } };
  };
  const apiMessage = candidate?.response?.data?.message;
  if (typeof apiMessage === "string" && apiMessage.trim()) return apiMessage;
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
};

export default function DeviationsTab({
  form,
  addressSystemId,
  userId = 0,
}: Readonly<DeviationsTabProps>) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const resolvedAddressSystemId =
    addressSystemId || selectedAddressSystem?.id || 0;
  const objectId = Number(form?.values?.objectId || form?.values?.id || 0);
  const formContractId = Number(
    form?.values?.contractId || form?.values?.job || 0,
  );
  const selectedContractId = formContractId;

  const { getTranslation } = useTranslation({
    pageId: 516,
    addressSystemId: resolvedAddressSystemId,
    autoLoad: true,
  });

  const [metadata, setMetadata] =
    useState<ObjectDeviationMetadataType>(emptyMetadata);
  const [deviations, setDeviations] = useState<
    ObjectDeviationListItemType[]
  >([]);
  const [editingDeviation, setEditingDeviation] =
    useState<ObjectDeviationType | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [metadataError, setMetadataError] = useState("");

  const showError = useCallback(
    (message: string) => {
      notifications.show({
        title: getTranslation("DEVIATION_TITLE_ERROR") || "Error",
        message,
        color: "red",
        autoClose: 5000,
        position: "top-center",
      });
    },
    [getTranslation],
  );

  const loadList = useCallback(
    async (contractId: number) => {
      if (
        !objectId ||
        !contractId ||
        !resolvedAddressSystemId ||
        !userId
      ) {
        setDeviations([]);
        return;
      }
      setLoading(true);
      try {
        setDeviations(
          await getDeviations(
            objectId,
            contractId,
            resolvedAddressSystemId,
            userId,
          ),
        );
      } catch {
        showError(
          getTranslation("DEVIATION_MESSAGE_LOAD_FAILED") ||
            "Failed to load deviations.",
        );
      } finally {
        setLoading(false);
      }
    },
    [
      getTranslation,
      objectId,
      resolvedAddressSystemId,
      showError,
      userId,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const loadInitialData = async () => {
      if (!objectId || !resolvedAddressSystemId || !userId) return;
      setLoading(true);
      setMetadataError("");
      try {
        const metadataResult = await getDeviationMetadata(
          resolvedAddressSystemId,
          userId,
        );
        if (cancelled) return;

        setMetadata(metadataResult);
      } catch (error) {
        if (cancelled) return;
        const message = getErrorMessage(
          error,
          getTranslation("DEVIATION_MESSAGE_CONFIGURATION_FAILED") ||
            "Deviation configuration could not be loaded.",
        );
        setMetadataError(message);
        showError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, [
    getTranslation,
    objectId,
    resolvedAddressSystemId,
    showError,
    userId,
  ]);

  useEffect(() => {
    if (selectedContractId > 0) loadList(selectedContractId);
  }, [loadList, selectedContractId]);

  const openNew = () => {
    setEditingDeviation(null);
    setModalOpened(true);
  };

  const openEdit = async (id: number) => {
    if (!selectedContractId) return;
    setLoading(true);
    try {
      setEditingDeviation(
        await getDeviation(
          id,
          objectId,
          selectedContractId,
          resolvedAddressSystemId,
          userId,
        ),
      );
      setModalOpened(true);
    } catch {
      showError(
        getTranslation("DEVIATION_MESSAGE_DETAIL_LOAD_FAILED") ||
          "Failed to load the deviation.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (request: UpsertObjectDeviationRequest) => {
    try {
      if (editingDeviation) {
        await updateDeviation(editingDeviation.id, request, userId);
      } else {
        await createDeviation(request, userId);
      }
      notifications.show({
        title: getTranslation("DEVIATION_TITLE_SUCCESS") || "Success",
        message:
          getTranslation("DEVIATION_MESSAGE_SAVED") ||
          "Deviation saved successfully.",
        color: "green",
        position: "top-center",
      });
      setModalOpened(false);
      setEditingDeviation(null);
      await loadList(selectedContractId);
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          getTranslation("DEVIATION_MESSAGE_SAVE_FAILED") ||
            "Failed to save the deviation.",
        ),
      );
      throw error;
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = window.confirm(
      getTranslation("DEVIATION_CONFIRM_DELETE") ||
        "Are you sure you want to delete this deviation?",
    );
    if (!confirmed) return;

    try {
      await deleteDeviation(
        id,
        objectId,
        selectedContractId,
        resolvedAddressSystemId,
        userId,
      );
      notifications.show({
        title: getTranslation("DEVIATION_TITLE_DELETED") || "Deleted",
        message:
          getTranslation("DEVIATION_MESSAGE_DELETED") ||
          "Deviation deleted successfully.",
        color: "green",
        position: "top-center",
      });
      await loadList(selectedContractId);
    } catch {
      showError(
        getTranslation("DEVIATION_MESSAGE_DELETE_FAILED") ||
          "Failed to delete the deviation.",
      );
    }
  };

  const handlePrint = async (id: number) => {
    setLoading(true);
    try {
      const detail = await getDeviation(
        id,
        objectId,
        selectedContractId,
        resolvedAddressSystemId,
        userId,
      );
      const blob = await pdf(
        <DeviationReportPDF
          deviation={detail}
          metadata={metadata}
          getTranslation={getTranslation}
        />,
      ).toBlob();
      const reportPart = detail.deviationNumber || String(detail.id);
      saveAs(
        blob,
        `deviation_${sanitizeForFilename(reportPart)}_${generateTimestamp()}.pdf`,
      );
    } catch {
      showError(
        getTranslation("DEVIATION_MESSAGE_PDF_FAILED") ||
          "Failed to generate the deviation PDF.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack h="100%" gap="md">
        <Group justify="space-between" align="end">
          <Title order={4}>
            {getTranslation("DEVIATION_TITLE_LIST") || "Deviations"}
          </Title>
          <Group>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={openNew}
              disabled={
                !selectedContractId ||
                metadata.types.length === 0 ||
                metadata.targets.length === 0
              }
            >
              {getTranslation("DEVIATION_ACTION_ADD") || "Add deviation"}
            </Button>
          </Group>
        </Group>

        {metadataError && (
          <Text c="red" size="sm">
            {metadataError}
          </Text>
        )}

        <Paper
          withBorder
          radius="md"
          className={styles.tableContainer}
        >
          {loading && deviations.length === 0 ? (
            <Group justify="center" p="xl">
              <Loader />
            </Group>
          ) : (
            <Table
              striped
              highlightOnHover
              className={styles.table}
            >
              <Table.Thead className={styles.tableHead}>
                <Table.Tr bg="var(--mantine-color-gray-3)">
                  <Table.Th w="20%" className={styles.headerCell}>
                    {getTranslation("DEVIATION_LABEL_DATE") || "Date"}
                  </Table.Th>
                  <Table.Th w="30%" className={styles.headerCell}>
                    {getTranslation("DEVIATION_LABEL_SUBJECT") || "Subject"}
                  </Table.Th>
                  <Table.Th w="30%" className={styles.headerCell}>
                    {getTranslation("DEVIATION_LABEL_MODIFIED_BY") ||
                      "Modified by"}
                  </Table.Th>
                  <Table.Th
                    w="20%"
                    ta="right"
                    className={styles.headerCell}
                  >
                    {getTranslation("DEVIATION_LABEL_ACTIONS") || "Actions"}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {deviations.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={4}>
                      <Text c="dimmed" ta="center" p="lg">
                        {getTranslation("DEVIATION_EMPTY") ||
                          "No deviations found."}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  deviations.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        {formatFinnishDate(new Date(item.deviationDate))}
                      </Table.Td>
                      <Table.Td>{item.deviationType}</Table.Td>
                      <Table.Td>
                        {item.modified
                          ? `${formatFinnishDate(new Date(item.modified))} / ${item.modifier}`
                          : item.modifier}
                      </Table.Td>
                      <Table.Td>
                        <Group justify="flex-end" gap="xs">
                          <ActionIcon
                            variant="subtle"
                            aria-label={
                              getTranslation("DEVIATION_ACTION_EDIT") || "Edit"
                            }
                            onClick={() => openEdit(item.id)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={
                              getTranslation("DEVIATION_ACTION_DELETE") ||
                              "Delete"
                            }
                            onClick={() => handleDelete(item.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            aria-label={
                              getTranslation("DEVIATION_ACTION_PRINT") ||
                              "Print PDF"
                            }
                            onClick={() => handlePrint(item.id)}
                          >
                            <IconPrinter size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      <AddEditDeviationModal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setEditingDeviation(null);
        }}
        onSave={handleSave}
        deviation={editingDeviation}
        metadata={metadata}
        objectId={objectId}
        contractId={selectedContractId}
        addressSystemId={resolvedAddressSystemId}
        getTranslation={getTranslation}
      />
    </>
  );
}
